// frontend/src/App.js
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import Modal from 'react-modal';
import axios from 'axios'; // Import axios
import './App.css';
import LoginPage from './LoginPage';
import AppExplorer from './AppExplorer';
import ConfigSection from './ConfigSection';
import ChatWindow from './ChatWindow';
import HomePage from './HomePage'; // <-- Import HomePage

// --- Modal Style (Keep existing) ---
const customModalStyles = {
    content: { top: '50%', left: '50%', right: 'auto', bottom: 'auto', marginRight: '-50%', transform: 'translate(-50%, -50%)', maxWidth: '80%', minWidth: '400px', maxHeight: '85vh', overflow: 'auto', padding: '25px', border: '1px solid #ccc', borderRadius: '8px', boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)', backgroundColor: '#fff', zIndex: 1001 },
    overlay: { backgroundColor: 'rgba(0, 0, 0, 0.6)', zIndex: 1000 },
};
Modal.setAppElement('#root');

// --- Default Settings and Provider/Model Map (moved to App.js context) ---
const defaultSettings = {
    llmProvider: 'OpenAI',
    model: 'gpt-4o-mini',
    temperature: 0.5,
    maxTokens: 2500,
    url: 'https://api.openai.com/v1/chat/completions', // Default URL
};

const providerModelMap = {
     OpenAI: [
         { name: 'gpt-4o-mini', url: 'https://api.openai.com/v1/chat/completions', model_max_tokens: '16000' },
         { name: 'gpt-4o', url: 'https://api.openai.com/v1/chat/completions', model_max_tokens: '128000'  },
         { name: 'o3-mini', url: 'https://api.openai.com/v1/chat/completions', model_max_tokens: '128000'  },
         { name: 'o4-mini', url: 'https://api.openai.com/v1/chat/completions', model_max_tokens: '128000'  },
         { name: 'o1-mini', url: 'https://api.openai.com/v1/chat/completions', model_max_tokens: '128000'  },
     ],
     Anthropic: [
         { name: 'claude-3-5-sonnet-latest', url: 'https://api.anthropic.com/v1/messages', model_max_tokens: '8192'  },
         { name: 'claude-3-opus-latest', url: 'https://api.anthropic.com/v1/messages', model_max_tokens: '4096'  },
         { name: 'claude-3-sonnet-20240229', url: 'https://api.anthropic.com/v1/messages', model_max_tokens: '4096'  },
         { name: 'claude-3-haiku-20240307', url: 'https://api.anthropic.com/v1/messages', model_max_tokens: '4096'  },
     ],
     OpenSource: [
         { name: 'meta-llama/llama-4-scout-17b-16e-instruct', url: '', model_max_tokens: '131072'  },
         { name: 'deepseek-r1-distill-llama-70b', url: '', model_max_tokens: '128000'  },
         { name: 'llama-3.1-8b-instant', url: '', model_max_tokens: '128000'  },
         { name: 'meta-llama/llama-4-maverick-17b-128e-instruct', url: '', model_max_tokens: '131072'  },
     ],
     Grok: [
        { name: 'grok-3-mini', url: '', model_max_tokens: '131072'  },
        { name: 'grok-4', url: '', model_max_tokens: '131072'  },
        { name: 'grok-3', url: '', model_max_tokens: '131072'  },
    ],
     Local: [
         { name: 'llama3:latest', url: 'http://localhost:11434/api/chat', model_max_tokens: '8192' },
     ]
 };


function App() {
    // --- Authentication State ---
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [authUsername, setAuthUsername] = useState('');
    const [isInitialLoading, setIsInitialLoading] = useState(true);

    const [theme, setTheme] = useState('LIGHT'); // Default to LIGHT theme

    // --- View State ---
    const [currentView, setCurrentView] = useState('home');

    // --- Shared State ---
    const [directories, setDirectories] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [userLLMSettings, setUserLLMSettings] = useState(null);

    // --- Header LLM Settings Editing State ---
    const [isEditingHeaderSettings, setIsEditingHeaderSettings] = useState(false);
    const [editableHeaderSettings, setEditableHeaderSettings] = useState(null);
    const [headerMaxTokenValue, setHeaderMaxTokenValue] = useState(defaultSettings.maxTokens);
    const [isSavingHeaderSettings, setIsSavingHeaderSettings] = useState(false);


    // --- Explorer Specific Config State (Managed Here) ---
    const [userRole, setUserRole] = useState('');
    const [initialQuery, setInitialQuery] = useState('');
    const [maxLoops, setMaxLoops] = useState(5);
    const [analyzeResponse, setAnalyzeResponse] = useState(false);
    const [verifyCheckin, setVerifyCheckin] = useState(1);
    const [useRAG, setUseRAG] = useState(false);

    // --- Conversation State ---
    const [conversationList, setConversationList] = useState([]);
    const [selectedLogId, setSelectedLogId] = useState('');

    // --- ADDED: Ref to access ChatWindow's imperative methods ---
    const chatWindowRef = useRef(null);

    // --- MODIFIED: `handleLogout` to check for unsaved chat ---
    const handleLogout = useCallback(async (clearAll = true) => {
        // Before doing anything, check if we're leaving an unsaved chat.
        if (currentView === 'chat' && chatWindowRef.current) {
            const canProceed = await chatWindowRef.current.handleBeforeNavigate();
            if (!canProceed) {
                setIsLoading(false); // Ensure loading state is correct if user cancels.
                return; // User chose not to proceed with logout.
            }
        }

        console.log("handleLogout called.");
        setIsLoading(true); setError(null);
        try {
            await axios.post('/api/logout', {}, { withCredentials: true });
        } catch (err) { console.error("Error during backend logout request:", err); }
         finally {
            setIsAuthenticated(false);
            setAuthUsername('');
            setDirectories([]);
            setConversationList([]);
            setCurrentView('home');
            setInitialQuery(''); setUserRole(''); setSelectedLogId('');
            setMaxLoops(5); setAnalyzeResponse(false); setVerifyCheckin(1); setUseRAG(false);
            setUserLLMSettings(null);
            setIsEditingHeaderSettings(false);
            setEditableHeaderSettings(null);
            setIsSavingHeaderSettings(false);
            setIsLoading(false);
            setTheme('LIGHT');
            console.log("User logged out.");
        }
    }, [currentView]); // <-- MODIFIED: Added `currentView` dependency


    const fetchConversations = useCallback(async () => {
        if (!isAuthenticated) return;
        console.log("Fetching conversations...");
        try {
            const convoResponse = await axios.get('/api/conversations', { withCredentials: true });
            setConversationList(convoResponse.data.conversations || []);
            console.log("Conversations fetched successfully.");
        } catch (err) {
            console.warn("Could not refresh conversation list:", err);
             if (err.response?.status === 401) {
                 console.warn("Session expired or invalid fetching conversations. Logging out.");
                 handleLogout(false);
             } else {
                 setError("Network error fetching conversations.");
             }
        }
    }, [isAuthenticated, handleLogout]);

    const fetchUserLLMSettings = useCallback(async (calledFrom = "unknown") => {
        if (!isAuthenticated) return;
        console.log(`App.js: Fetching user LLM settings (called from ${calledFrom})...`);
        try {
            const settingsResponse = await axios.get('/api/user/settings', { withCredentials: true });
            const fetchedSettings = settingsResponse.data;
            if (fetchedSettings) {
                // Parsing logic from original fetchInitialAppData
                const parsedSettings = {
                    ...fetchedSettings,
                    llmProvider: fetchedSettings.llmprovider || defaultSettings.llmProvider,
                    model: fetchedSettings.model || defaultSettings.model,
                    temperature: parseFloat(fetchedSettings.temperature),
                    max_tokens: parseInt(fetchedSettings.max_tokens, 10),
                    // chat_settings is already an object from backend if it exists
                    chat_settings: fetchedSettings.chat_settings || {}, // Ensure chat_settings is an object
                };
                if (isNaN(parsedSettings.temperature)) parsedSettings.temperature = defaultSettings.temperature;
                if (isNaN(parsedSettings.max_tokens)) parsedSettings.max_tokens = defaultSettings.maxTokens;

                const providerModels = providerModelMap[parsedSettings.llmProvider];
                const modelDetails = providerModels?.find(m => m.name === parsedSettings.model);
                parsedSettings.url = modelDetails?.url || (parsedSettings.llmProvider === defaultSettings.llmProvider ? defaultSettings.url : '');
                
                setUserLLMSettings(parsedSettings);
                console.log("App.js: User LLM Settings set/refreshed:", parsedSettings);
            } else {
                setUserLLMSettings(null); // Or set to defaults if backend returns nothing
                console.log("App.js: No settings data received from backend, userLLMSettings set to null.");
            }
        } catch (e) {
            console.error("App.js: Error fetching/parsing user settings:", e);
            if (e.response?.status === 401) {
                 handleLogout(false); setError('Session expired. Please log in.');
            } else if (e.response?.status === 404) {
                console.warn("App.js: User LLM settings not found on backend. Using defaults.");
                setUserLLMSettings({ ...defaultSettings, chat_settings: {} }); // Fallback to defaults
            } else {
                setError(`Failed to fetch user settings: ${e.message}.`);
            }
        }
    }, [isAuthenticated, handleLogout]); // Added handleLogout to dependencies

    const fetchInitialAppData = useCallback(async () => {
        if (!isAuthenticated) return;
        console.log("App.js: Fetching initial app data...");
        setIsLoading(true); setError(null);
        try {
            const dirResponse = await axios.get('/api/directories', { withCredentials: true });
            setDirectories(dirResponse.data.directories || []);
            await fetchConversations();
            await fetchUserLLMSettings("fetchInitialAppData"); // Use the new specific function
        } catch (e) {
            console.error("App.js: Error fetching initial app data:", e);
            // Error handling for dir/convo fetch is still relevant here
            if (e.response?.status === 401 && e.config.url !== '/api/user/settings') { // Avoid double logout if settings fetch failed
                 handleLogout(false); setError('Session expired. Please log in.');
            } else if (e.config.url !== '/api/user/settings') {
                 setError(`Failed to fetch initial app data: ${e.message}.`);
            }
        } finally { setIsLoading(false); }
    }, [isAuthenticated, fetchConversations, fetchUserLLMSettings, handleLogout]); // Added handleLogout


    useEffect(() => {
        const checkSession = async () => {
            setIsInitialLoading(true);
            try {
                const response = await axios.get('/api/check_session', { withCredentials: true });
                if (response.data.isAuthenticated) {
                    setIsAuthenticated(true); 
                    setAuthUsername(response.data.username);
                    if (response.data.theme) {
                        setTheme(response.data.theme);
                    }
                } else { 
                    setIsAuthenticated(false); 
                    setAuthUsername(''); 
                    setTheme('LIGHT');
                }
            } catch (err) { 
                setIsAuthenticated(false); 
                setAuthUsername(''); 
                setTheme('LIGHT');
            }
            finally { setIsInitialLoading(false); }
        };
        checkSession();
    }, []);
    
    useEffect(() => {
        document.body.className = '';
        document.body.classList.add(`theme-${theme.toLowerCase()}`);
        console.log(`Theme set to: ${theme}`);
    }, [theme]); 

    useEffect(() => {
        if (isAuthenticated && !isInitialLoading) {
            fetchInitialAppData();
        }
        if (!isAuthenticated && !isInitialLoading) {
            setUserLLMSettings(null);
            setIsEditingHeaderSettings(false);
            setEditableHeaderSettings(null);
        }
    }, [isAuthenticated, isInitialLoading, fetchInitialAppData]);

    useEffect(() => {
        if (isEditingHeaderSettings) {
            const currentGlobalSettings = userLLMSettings || defaultSettings;
            setEditableHeaderSettings({ ...currentGlobalSettings });
        } else {
            setEditableHeaderSettings(null);
        }
    }, [isEditingHeaderSettings, userLLMSettings]);

    useEffect(() => {
        const settingsSource = isEditingHeaderSettings && editableHeaderSettings ? editableHeaderSettings : userLLMSettings;
        if (settingsSource && settingsSource.llmProvider && settingsSource.model) {
            const providerModels = providerModelMap[settingsSource.llmProvider];
            const currentModelDetails = providerModels?.find(m => m.name === settingsSource.model);
            const newMax = currentModelDetails ? parseInt(currentModelDetails.model_max_tokens, 10) : defaultSettings.maxTokens;
            setHeaderMaxTokenValue(isNaN(newMax) || newMax <=0 ? defaultSettings.maxTokens : newMax);
        } else {
            setHeaderMaxTokenValue(defaultSettings.maxTokens);
        }
    }, [editableHeaderSettings, userLLMSettings, providerModelMap, isEditingHeaderSettings]);

    useEffect(() => {
        document.body.className = '';
        document.body.classList.add(`theme-${theme.toLowerCase()}`);
    }, [theme]);

    const handleSettingsSavedInChatWindow = useCallback(() => {
        console.log("App.js: Settings saved in ChatWindow, triggering refresh of userLLMSettings.");
        fetchUserLLMSettings("handleSettingsSavedInChatWindow");
    }, [fetchUserLLMSettings]);

    const handleEditableHeaderChange = useCallback((key, value) => {
        setEditableHeaderSettings(prev => {
            let newSettings = { ...(prev || defaultSettings) };

            if (key === 'llmProvider') {
                const firstModelOfNewProvider = providerModelMap[value]?.[0];
                if (firstModelOfNewProvider) {
                    const modelMax = parseInt(firstModelOfNewProvider.model_max_tokens, 10) || defaultSettings.maxTokens;
                    newSettings = {
                        ...newSettings,
                        llmProvider: value,
                        model: firstModelOfNewProvider.name,
                        url: firstModelOfNewProvider.url,
                        maxTokens: Math.min(newSettings.maxTokens || defaultSettings.maxTokens, modelMax)
                    };
                } else {
                    newSettings = { ...newSettings, llmProvider: value, model: '', url: '', maxTokens: defaultSettings.maxTokens };
                }
            } else if (key === 'model') {
                const providerModels = providerModelMap[newSettings.llmProvider];
                const selectedModelDetails = providerModels?.find(m => m.name === value);
                if (selectedModelDetails) {
                    const modelMax = parseInt(selectedModelDetails.model_max_tokens, 10) || defaultSettings.maxTokens;
                    newSettings = {
                        ...newSettings,
                        model: value,
                        url: selectedModelDetails.url,
                        maxTokens: Math.min(newSettings.maxTokens || defaultSettings.maxTokens, modelMax)
                    };
                } else {
                    newSettings = { ...newSettings, model: value };
                }
            } else if (key === 'temperature') {
                const numericValue = parseFloat(value);
                if (!isNaN(numericValue)) {
                    newSettings = { ...newSettings, temperature: Math.max(0, Math.min(2, numericValue)) };
                }
            } else if (key === 'maxTokens') {
                const numericValue = parseInt(value, 10);
                 if (!isNaN(numericValue)) {
                    const currentProviderModels = providerModelMap[newSettings.llmProvider];
                    const currentModelInfo = currentProviderModels?.find(m => m.name === newSettings.model);
                    const modelMaxLimit = currentModelInfo ? parseInt(currentModelInfo.model_max_tokens, 10) : headerMaxTokenValue;
                    const cappedValue = Math.min(numericValue, modelMaxLimit || 1);
                    newSettings = { ...newSettings, maxTokens: Math.max(1, cappedValue) };
                 }
            }
            return newSettings;
        });
    }, [providerModelMap, headerMaxTokenValue]);

    const toggleHeaderSettingsEdit = () => {
        if (isEditingHeaderSettings) {
            setEditableHeaderSettings(null);
        }
        setIsEditingHeaderSettings(!isEditingHeaderSettings);
        setError(null);
    };

    const saveHeaderSettings = async () => {
        if (!editableHeaderSettings) return;
        setIsSavingHeaderSettings(true); setError(null);
        try {
            const payload = {
                llmProvider: editableHeaderSettings.llmProvider,
                model: editableHeaderSettings.model,
                temperature: editableHeaderSettings.temperature.toString(),
                maxTokens: editableHeaderSettings.maxTokens.toString(),
            };
            await axios.post('/api/user/settings', payload, { withCredentials: true });

            const updatedSettingsResponse = await axios.get('/api/user/settings', { withCredentials: true });
            const fetchedSettings = updatedSettingsResponse.data;
            if (fetchedSettings) {
                const parsedSettings = {
                    ...fetchedSettings,
                    llmProvider: fetchedSettings.llmprovider,
                    model: fetchedSettings.model,
                    temperature: parseFloat(fetchedSettings.temperature),
                    max_tokens: parseInt(fetchedSettings.max_tokens, 10)
                };
                const providerModels = providerModelMap[parsedSettings.llmProvider];
                const modelDetails = providerModels?.find(m => m.name === parsedSettings.model);
                parsedSettings.url = modelDetails?.url || (parsedSettings.llmProvider === defaultSettings.llmProvider ? defaultSettings.url : '');

                if (isNaN(parsedSettings.temperature)) parsedSettings.temperature = defaultSettings.temperature;
                if (isNaN(parsedSettings.max_tokens)) parsedSettings.max_tokens = defaultSettings.maxTokens;

                setUserLLMSettings(parsedSettings);
                console.log("Saved and Refreshed User LLM Settings:", parsedSettings);
            } else {
                console.warn("Failed to re-fetch settings after save, using local editable state.");
                setUserLLMSettings({ ...editableHeaderSettings });
            }

            setIsEditingHeaderSettings(false);
        } catch (err) {
            console.error("Error saving header settings:", err);
            const errorMsg = err.response?.data?.error || err.message || "Failed to save settings.";
            setError(`Save failed: ${errorMsg}`);
        } finally {
            setIsSavingHeaderSettings(false);
        }
    };

     const updateUserLLMSettings = (newSettings) => {
        console.log("App.js: Updating userLLMSettings from ConfigSection:", newSettings);
        const consistentSettings = {
            ...newSettings,
            llmProvider: newSettings.llmprovider || newSettings.llmProvider,
            maxTokens: newSettings.max_tokens || newSettings.maxTokens,
            chat_settings: newSettings.chat_settings || userLLMSettings?.chat_settings || {},
        };
        delete consistentSettings.llmprovider; 
        delete consistentSettings.max_tokens; 

        setUserLLMSettings(consistentSettings);
    };

    const handleSaveConfig = useCallback((newConfig) => {
        setMaxLoops(newConfig.maxLoops);
        setAnalyzeResponse(newConfig.analyzeResponse);
        setVerifyCheckin(newConfig.verifyCheckin);
        setUseRAG(newConfig.useRAG);
    }, []);

    // --- ADDED: Navigation Guard Function ---
    const handleBeforeNavigate = async () => {
        if (currentView === 'chat' && chatWindowRef.current) {
            const canProceed = await chatWindowRef.current.handleBeforeNavigate();
            if (!canProceed) {
                return false; // Navigation blocked by user or save failure
            }
        }
        return true; // Navigation is allowed
    };

    // --- MODIFIED: Navigation functions to be async and use the guard ---
    const navigateToHome = async () => {
        if (!(await handleBeforeNavigate())) return;
        setCurrentView('home');
        setIsEditingHeaderSettings(false);
    };
    const navigateToExplorer = async () => {
        if (!(await handleBeforeNavigate())) return;
        setCurrentView('explorer');
        setIsEditingHeaderSettings(false);
    };
    const navigateToConfig = async () => {
        if (!(await handleBeforeNavigate())) return;
        setCurrentView('config');
        setIsEditingHeaderSettings(false);
    };
    const navigateToChat = () => setCurrentView('chat');

    if (isInitialLoading) { return <div className="loading-fullpage">Checking session...</div>; }
    if (!isAuthenticated) { return <LoginPage setIsAuthenticated={setIsAuthenticated} setAuthUsername={setAuthUsername} />; }

    const getHeaderTitle = () => {
        switch(currentView) {
            case 'home': return 'Home'; case 'explorer': return 'Explorer';
            case 'config': return 'Config'; case 'chat': return 'Chat';
            default: return 'App';
        }
    };

    const availableModelsForHeader = editableHeaderSettings?.llmProvider ? (providerModelMap[editableHeaderSettings.llmProvider] || []) : [];
    const tempPercentForHeader = editableHeaderSettings ? ((editableHeaderSettings.temperature || 0) / 2) * 100 : 50;
    const tokenPercentForHeader = editableHeaderSettings && headerMaxTokenValue > 1 ? (((editableHeaderSettings.maxTokens || 1) - 1) / (headerMaxTokenValue - 1)) * 100 : 0;


    return (
        <div className="App">
            <header className="app-header">
                 <h1>
                    <img
                        src={theme === 'LIGHT' ? `${process.env.PUBLIC_URL}/Pearlify_light_logo_2.PNG` : `${process.env.PUBLIC_URL}/Pearlify_dark_logo_2.PNG`}
                        alt="Pearlify Logo"
                        className="header-logo"
                    />
                    Pearlify {getHeaderTitle()}
                 </h1>

                 {currentView === 'chat' && (
                    isEditingHeaderSettings && editableHeaderSettings ? (
                        <div className="header-llm-settings-edit">
                            <div className="header-setting-item">
                                <label htmlFor="header-llmProvider">Provider</label>
                                <select id="header-llmProvider" value={editableHeaderSettings.llmProvider} onChange={(e) => handleEditableHeaderChange('llmProvider', e.target.value)} disabled={isSavingHeaderSettings}>
                                    {Object.keys(providerModelMap).map(p => <option key={p} value={p}>{p}</option>)}
                                </select>
                            </div>
                            <div className="header-setting-item">
                                <label htmlFor="header-llmModel">Model</label>
                                <select id="header-llmModel" value={editableHeaderSettings.model} onChange={(e) => handleEditableHeaderChange('model', e.target.value)} disabled={isSavingHeaderSettings || !editableHeaderSettings.llmProvider || availableModelsForHeader.length === 0}>
                                    {availableModelsForHeader.length > 0 ?
                                        availableModelsForHeader.map(m => <option key={m.name} value={m.name}>{m.name}</option>) :
                                        <option value="">N/A</option>}
                                </select>
                            </div>
                            <div className="header-setting-item slider">
                                <label htmlFor="header-temp">Temp: <span>{editableHeaderSettings.temperature?.toFixed(2)}</span></label>
                                <input type="range" id="header-temp" min="0" max="2" step="0.05" value={editableHeaderSettings.temperature} onChange={(e) => handleEditableHeaderChange('temperature', parseFloat(e.target.value))} disabled={isSavingHeaderSettings} style={{ '--value-percent': `${tempPercentForHeader}%` }}/>
                            </div>
                            <div className="header-setting-item slider">
                                <label htmlFor="header-tokens">MaxT: <span>{editableHeaderSettings.maxTokens?.toLocaleString()}</span> <small>(Limit: {headerMaxTokenValue?.toLocaleString()})</small></label>
                                <input type="range" id="header-tokens" min="1" max={headerMaxTokenValue} step={headerMaxTokenValue > 1000 ? 50 : 1} value={editableHeaderSettings.maxTokens} onChange={(e) => handleEditableHeaderChange('maxTokens', parseInt(e.target.value,10))} disabled={isSavingHeaderSettings || !headerMaxTokenValue || headerMaxTokenValue <= 1} style={{ '--value-percent': `${tokenPercentForHeader}%` }}/>
                            </div>
                            <div className="header-setting-actions">
                                <button onClick={saveHeaderSettings} disabled={isSavingHeaderSettings} className="header-save-button">
                                    {isSavingHeaderSettings ? 'Saving...' : 'Save'}
                                </button>
                                <button onClick={toggleHeaderSettingsEdit} disabled={isSavingHeaderSettings} className="header-cancel-button">Cancel</button>
                            </div>
                        </div>
                    ) : userLLMSettings ? (
                        <div className="header-llm-settings" title={`Provider: ${userLLMSettings.llmProvider}\nModel: ${userLLMSettings.model}\nTemp: ${userLLMSettings.temperature}\nMax Tokens: ${userLLMSettings.max_tokens}`}>
                            <span className="setting-item">
                                <span className="setting-label">Provider:</span>
                                <span className="setting-value">{userLLMSettings.llmProvider}</span>
                            </span>
                            <span className="setting-item">
                                <span className="setting-label">Model:</span>
                                <span className="setting-value model">{userLLMSettings.model}</span>
                            </span>
                            <span className="setting-item">
                                <span className="setting-label">Temp:</span>
                                <span className="setting-value">
                                    {typeof userLLMSettings.temperature === 'number' ? userLLMSettings.temperature.toFixed(1) : 'N/A'}
                                </span>
                            </span>
                            <span className="setting-item">
                                <span className="setting-label">MaxT:</span>
                                <span className="setting-value">
                                    {typeof userLLMSettings.max_tokens === 'number' ? userLLMSettings.max_tokens.toLocaleString() : 'N/A'}
                                </span>
                            </span>
                            <button onClick={toggleHeaderSettingsEdit} className="header-edit-button" title="Edit LLM Settings">
                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16"><path d="M15.502 1.94a.5.5 0 0 1 0 .706L14.459 3.69l-2-2L13.502.646a.5.5 0 0 1 .707 0l1.293 1.293zm-1.75 2.456-2-2L4.939 9.21a.5.5 0 0 0-.121.196l-.805 2.414a.25.25 0 0 0 .316.316l2.414-.805a.5.5 0 0 0 .196-.12l6.813-6.814z"/><path fillRule="evenodd" d="M1 13.5A1.5 1.5 0 0 0 2.5 15h11a1.5 1.5 0 0 0 1.5-1.5v-6a.5.5 0 0 0-1 0v6a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5v-11a.5.5 0 0 1 .5-.5H9a.5.5 0 0 0 0-1H2.5A1.5 1.5 0 0 0 1 2.5z"/></svg>
                            </button>
                        </div>
                    ) : (
                        <div className="header-llm-settings-empty">
                            <span>LLM settings not set.</span>
                            <button onClick={toggleHeaderSettingsEdit} className="header-edit-button" title="Configure LLM Settings">Configure</button>
                        </div>
                    )
                 )}

                 <div className="header-controls">
                     <span className="welcome-user">Welcome, {authUsername || 'User'}!</span>
                    
                    {currentView !== 'home' && (
                        <button onClick={navigateToHome} className="nav-button" disabled={isLoading}>Home</button>
                    )}

                    {currentView === 'home' ? (
                        // On Home page, show buttons to go to other main sections
                        <>
                            <button onClick={navigateToExplorer} className="nav-button" disabled={isLoading}>Explorer</button>
                            <button onClick={navigateToChat} className="nav-button" disabled={isLoading}>Chat</button>
                            <button onClick={navigateToConfig} className="nav-button" disabled={isLoading}>Config</button>
                        </>
                    ) : (
                        // On other pages (Explorer, Chat, Config), show buttons to other main sections
                        <>
                            {currentView !== 'explorer' && (
                                <button onClick={navigateToExplorer} className="nav-button" disabled={isLoading}>Explorer</button>
                            )}
                            {currentView !== 'chat' && (
                                <button onClick={navigateToChat} className="nav-button" disabled={isLoading}>Chat</button>
                            )}
                            {currentView !== 'config' && (
                                <button onClick={navigateToConfig} className="nav-button" disabled={isLoading}>Config</button>
                            )}
                        </>
                    )}
                     <button onClick={() => handleLogout(true)} className="logout-button" disabled={isLoading}> Logout </button>
                 </div>
            </header>

            {error && <div className="error app-global-error" onClick={() => setError(null)} title="Click to dismiss error">{error}</div>}


            {currentView === 'home' && (
                <HomePage navigateToExplorer={navigateToExplorer} navigateToChat={navigateToChat} navigateToConfig={navigateToConfig} authUsername={authUsername} theme={theme} />
            )}
            {currentView === 'explorer' && (
                <AppExplorer
                    isAuthenticated={isAuthenticated} directories={directories} userRole={userRole} setUserRole={setUserRole}
                    initialQuery={initialQuery} setInitialQuery={setInitialQuery} maxLoops={maxLoops} analyzeResponse={analyzeResponse}
                    verifyCheckin={verifyCheckin} useRAG={useRAG} handleSaveConfig={handleSaveConfig}
                    conversationList={conversationList} selectedLogId={selectedLogId} setSelectedLogId={setSelectedLogId}
                    fetchConversations={fetchConversations} handleLogout={handleLogout} isLoading={isLoading} setIsLoading={setIsLoading}
                    error={error} setError={setError}
                />
            )}
            {currentView === 'config' && (
                <ConfigSection
                    isAuthenticated={isAuthenticated} directories={directories} navigateToExplorer={navigateToExplorer}
                    isLoading={isLoading} setIsLoading={setIsLoading} error={error} setError={setError}
                    authUsername={authUsername}
                    handleLogout={handleLogout} 
                    updateUserLLMSettings={updateUserLLMSettings}
                    theme={theme}
                    setTheme={setTheme}
                />
            )}
            {currentView === 'chat' && (
                <ChatWindow
                    ref={chatWindowRef} // <-- MODIFIED: Attach the ref
                    isAuthenticated={isAuthenticated}
                    handleLogout={handleLogout}
                    authUsername={authUsername}
                    userLLMSettings={userLLMSettings}
                    onSettingsSavedInChatWindow={handleSettingsSavedInChatWindow}
                />
            )}
        </div>
    );
}

export default App;